import { coerceStoredJsonValue } from "./coerce-stored-json.js";

/** Malaysian states and federal territories for shipping address dropdowns. */
export const MY_STATES = [
  { code: "JHR", label: "Johor" },
  { code: "KDH", label: "Kedah" },
  { code: "KTN", label: "Kelantan" },
  { code: "MLK", label: "Melaka" },
  { code: "NSN", label: "Negeri Sembilan" },
  { code: "PHG", label: "Pahang" },
  { code: "PRK", label: "Perak" },
  { code: "PLS", label: "Perlis" },
  { code: "PNG", label: "Pulau Pinang" },
  { code: "SBH", label: "Sabah" },
  { code: "SWK", label: "Sarawak" },
  { code: "SGR", label: "Selangor" },
  { code: "TRG", label: "Terengganu" },
  { code: "KUL", label: "Wilayah Persekutuan Kuala Lumpur" },
  { code: "LBN", label: "Wilayah Persekutuan Labuan" },
  { code: "PJY", label: "Wilayah Persekutuan Putrajaya" },
] as const;

export type MyStateCode = (typeof MY_STATES)[number]["code"];
export type MyStateLabel = (typeof MY_STATES)[number]["label"];

export const MY_STATE_LABELS: MyStateLabel[] = MY_STATES.map((s) => s.label);

/** For Zod `z.enum()` — canonical state labels. */
export const MY_STATE_LABELS_TUPLE = MY_STATE_LABELS as unknown as [MyStateLabel, ...MyStateLabel[]];

export type ShippingStateRate = {
  state: string;
  fee: number;
};

export function normalizeMyState(input: string | null | undefined): MyStateLabel | null {
  const trimmed = input?.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  const match = MY_STATES.find((s) => s.label.toLowerCase() === lower);
  return match?.label ?? null;
}

export function defaultStateRates(fallbackFee = 10): ShippingStateRate[] {
  return MY_STATES.map((s) => ({ state: s.label, fee: fallbackFee }));
}

export function mergeStateRates(stored: unknown, fallbackFee = 10): ShippingStateRate[] {
  stored = coerceStoredJsonValue(stored);
  const byState = new Map<string, number>();
  if (Array.isArray(stored)) {
    for (const row of stored) {
      if (!row || typeof row !== "object") continue;
      const state = String((row as ShippingStateRate).state ?? "").trim();
      const fee = Number((row as ShippingStateRate).fee);
      const canonical = normalizeMyState(state);
      if (canonical && Number.isFinite(fee) && fee >= 0) {
        byState.set(canonical, fee);
      }
    }
  }
  return MY_STATES.map((s) => ({
    state: s.label,
    fee: byState.get(s.label) ?? fallbackFee,
  }));
}

export function resolveStateShippingFee(opts: {
  state: string | null | undefined;
  rates: ShippingStateRate[];
  fallbackFee: number;
  freeAbove: number;
  subtotal: number;
}): number {
  const { state, rates, fallbackFee, freeAbove, subtotal } = opts;
  if (freeAbove > 0 && subtotal >= freeAbove) return 0;

  const canonical = normalizeMyState(state);
  if (!canonical) return fallbackFee;

  const row = rates.find((r) => r.state === canonical);
  return row?.fee ?? fallbackFee;
}

/** ISO-3166 alpha-2 codes supported in address/shipping dropdowns. */
export const SHIPPING_COUNTRIES = [
  { code: "MY", label: "Malaysia" },
  { code: "SG", label: "Singapore" },
  { code: "BN", label: "Brunei" },
  { code: "TH", label: "Thailand" },
  { code: "ID", label: "Indonesia" },
  { code: "PH", label: "Philippines" },
  { code: "VN", label: "Vietnam" },
  { code: "CN", label: "China" },
  { code: "HK", label: "Hong Kong" },
  { code: "TW", label: "Taiwan" },
  { code: "JP", label: "Japan" },
  { code: "KR", label: "South Korea" },
  { code: "AU", label: "Australia" },
  { code: "NZ", label: "New Zealand" },
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
] as const;

export type ShippingCountryCode = (typeof SHIPPING_COUNTRIES)[number]["code"];

export type ShippingCountryRate = {
  country: string;
  fee: number;
};

export function normalizeCountryCode(input: string | null | undefined): ShippingCountryCode | null {
  const trimmed = input?.trim().toUpperCase();
  if (!trimmed || trimmed.length !== 2) return null;
  return SHIPPING_COUNTRIES.some((c) => c.code === trimmed) ? (trimmed as ShippingCountryCode) : null;
}

export function shippingCountryLabel(code: string | null | undefined): string {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return code?.trim() || "—";
  return SHIPPING_COUNTRIES.find((c) => c.code === normalized)?.label ?? normalized;
}

export function mergeCountryRates(stored: unknown): ShippingCountryRate[] {
  stored = coerceStoredJsonValue(stored);
  if (!Array.isArray(stored)) return [];
  const rows: ShippingCountryRate[] = [];
  for (const row of stored) {
    if (!row || typeof row !== "object") continue;
    const country = normalizeCountryCode(String((row as ShippingCountryRate).country ?? ""));
    const fee = Number((row as ShippingCountryRate).fee);
    if (!country || country === "MY" || !Number.isFinite(fee) || fee < 0) continue;
    if (rows.some((r) => r.country === country)) continue;
    rows.push({ country, fee });
  }
  return rows.sort((a, b) => a.country.localeCompare(b.country));
}

export function resolveShippingFee(opts: {
  country: string | null | undefined;
  state: string | null | undefined;
  countryRates: ShippingCountryRate[];
  stateRates: ShippingStateRate[];
  fallbackFee: number;
  freeAbove: number;
  subtotal: number;
}): number {
  const { countryRates, stateRates, fallbackFee, freeAbove, subtotal } = opts;
  if (freeAbove > 0 && subtotal >= freeAbove) return 0;

  const country = normalizeCountryCode(opts.country) ?? "MY";
  if (country === "MY") {
    return resolveStateShippingFee({
      state: opts.state,
      rates: stateRates,
      fallbackFee,
      freeAbove: 0,
      subtotal,
    });
  }

  const row = countryRates.find((r) => r.country === country);
  return row?.fee ?? fallbackFee;
}
